import React, { forwardRef } from 'react';
import clsx from 'clsx';
import { LoadingSpinner } from './LoadingSpinner';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual variant */
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  /** Button size */
  size?: 'sm' | 'md' | 'lg';
  /** Show loading spinner and disable interaction */
  isLoading?: boolean;
  /** Icon rendered before the label */
  leftIcon?: React.ReactNode;
  /** Icon rendered after the label */
  rightIcon?: React.ReactNode;
}

const baseClasses = [
  'inline-flex items-center justify-center gap-2',
  'font-medium rounded-[var(--volt-radius-md)]',
  'transition-all duration-[var(--volt-transition-fast)]',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--volt-blue)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--volt-dark)]',
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
  'select-none',
].join(' ');

const variantClasses: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary: [
    'bg-[var(--volt-blue)] text-white',
    'hover:brightness-110 hover:shadow-[var(--volt-glow-blue)]',
    'active:brightness-90',
  ].join(' '),
  secondary: [
    'bg-[var(--volt-dark-elevated)] text-[var(--volt-text-secondary)]',
    'border border-[var(--volt-dark-border)]',
    'hover:border-[var(--volt-blue)] hover:text-[var(--volt-blue)]',
    'active:bg-[var(--volt-dark-surface)]',
  ].join(' '),
  ghost: [
    'bg-transparent text-[var(--volt-text-muted)]',
    'hover:bg-[var(--volt-dark-elevated)] hover:text-[var(--volt-text-primary)]',
    'active:bg-[var(--volt-dark-surface)]',
  ].join(' '),
  danger: [
    'bg-[var(--volt-error)] text-white',
    'hover:brightness-110 hover:shadow-[0_0_20px_rgba(239,68,68,0.3)]',
    'active:brightness-90',
  ].join(' '),
};

const sizeClasses: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
};

const spinnerSizes: Record<NonNullable<ButtonProps['size']>, 'sm' | 'md'> = {
  sm: 'sm',
  md: 'sm',
  lg: 'md',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      className,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        className={clsx(
          baseClasses,
          variantClasses[variant],
          sizeClasses[size],
          className,
        )}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <LoadingSpinner
            size={spinnerSizes[size]}
            color={variant === 'secondary' || variant === 'ghost' ? 'var(--volt-text-muted)' : 'currentColor'}
          />
        ) : (
          leftIcon
        )}
        {children}
        {!isLoading && rightIcon}
      </button>
    );
  },
);

Button.displayName = 'Button';
